import { useCallback, useEffect, useState } from "react";

/**
 * Connection health, as the kiosk means it: not "is there a network adapter"
 * but "did the last thing we tried actually reach the server".
 *
 * `navigator.onLine` is necessary but nowhere near sufficient — a tablet on
 * branch wifi with a dead uplink reports online all day. So the last real
 * request outcome overrides it, and going offline at the OS level resets the
 * optimism immediately.
 */
export function useOnline() {
  const [browserOnline, setBrowserOnline] = useState(() => navigator.onLine);
  const [lastRequestOk, setLastRequestOk] = useState<boolean | null>(null);

  useEffect(() => {
    const goOnline = () => setBrowserOnline(true);
    const goOffline = () => {
      setBrowserOnline(false);
      setLastRequestOk(false);
    };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const reportRequest = useCallback((ok: boolean) => {
    setLastRequestOk(ok);
    if (ok) setBrowserOnline(true);
  }, []);

  // Unknown (no request yet) counts as online: a freshly-booted kiosk should
  // not greet the branch with a red warning it has no evidence for.
  const online = browserOnline && lastRequestOk !== false;

  return { online, browserOnline, lastRequestOk, reportRequest };
}
